import { useState, useMemo } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { authorizedFetch } from "@/lib/api"
import { API_URL } from "@/lib/mutation-utils"

// Minimum shape a TripRow must satisfy to drive the interconnect feature.
// Kept as a structural interface so any page's TripRow is compatible without casting.
export interface InterconnectTripBase {
  id:                  string
  internalId:          number
  driverId:            string | null
  driverName:          string | null
  truckLicensePlate:   string | null
  trailerLicensePlate: string | null
  originCityId:        string | null
  originCityName:      string | null
  destinationCityId:   string | null
  destinationCityName: string | null
  departureAt:              string | null
  arrivalAt:               string | null
  scheduledOriginArrivalAt: string | null
}

export interface InterconnectPreview {
  tripA:            InterconnectTripBase
  tripB:            InterconnectTripBase
  // Null while the preview data is still loading from the API.
  interconnectDate: string | null
  routeKm:          number | null
  isLoading:        boolean
  // Non-null when the pair cannot be created — dialog shows the reason instead of the form.
  blockReason:      string | null
}

interface UseInterconnectOptions {
  // Query keys to invalidate after a successful creation.
  invalidateKeys: unknown[][]
  // Optional callback after a successful creation (e.g. clear selectedIds).
  onSuccess?: () => void
}

// Level 1: can the button open the dialog at all?
// Requires exactly 2 trips selected with the same non-null driverId.
export function deriveCandidatePair(
  allTrips: InterconnectTripBase[],
  selectedIds: Set<string>,
): { tripA: InterconnectTripBase; tripB: InterconnectTripBase } | null {
  if (selectedIds.size !== 2) return null
  const selected = allTrips.filter(t => selectedIds.has(t.id))
  if (selected.length !== 2) return null
  const [a, b] = selected as [InterconnectTripBase, InterconnectTripBase]
  if (!a.driverId || !b.driverId || a.driverId !== b.driverId) return null
  // Sort by effective date (scheduledOriginArrivalAt ?? departureAt) so tripA is the earlier leg.
  // Fall back to internalId if both dates are identical.
  const aTs = (a.scheduledOriginArrivalAt ?? a.departureAt) ? new Date((a.scheduledOriginArrivalAt ?? a.departureAt)!).getTime() : 0
  const bTs = (b.scheduledOriginArrivalAt ?? b.departureAt) ? new Date((b.scheduledOriginArrivalAt ?? b.departureAt)!).getTime() : 0
  const aFirst = aTs !== bTs ? aTs < bTs : a.internalId < b.internalId
  return aFirst
    ? { tripA: a, tripB: b }
    : { tripA: b, tripB: a }
}

// Level 2: given a valid candidate pair, is there a reason it cannot be created?
// Returns a human-readable message, or null if creation is allowed.
export function getBlockReason(
  tripA: InterconnectTripBase,
  tripB: InterconnectTripBase,
): string | null {
  // Accept scheduledOriginArrivalAt as a valid date source, falling back to departureAt.
  const effA = tripA.scheduledOriginArrivalAt ?? tripA.departureAt
  const effB = tripB.scheduledOriginArrivalAt ?? tripB.departureAt
  if (!effA || !effB) {
    const missing = [
      !effA && `#${tripA.internalId}`,
      !effB && `#${tripB.internalId}`,
    ].filter(Boolean).join(" y ")
    return `El viaje ${missing} no tiene fecha de salida ni llegada proyectada registrada.`
  }
  if (
    tripA.destinationCityId &&
    tripB.originCityId &&
    tripA.destinationCityId === tripB.originCityId
  ) {
    return `El destino de #${tripA.internalId} y el origen de #${tripB.internalId} son la misma ciudad (${tripA.destinationCityName ?? "—"}). No se puede crear un viaje entre el mismo punto.`
  }
  return null
}

export function useInterconnect(
  trips: InterconnectTripBase[],
  selectedIds: Set<string>,
  options: UseInterconnectOptions,
) {
  const qc = useQueryClient()
  const [preview, setPreview] = useState<InterconnectPreview | null>(null)

  // Non-null when the button should be enabled (same driver, exactly 2 selected).
  // Does NOT enforce date/city constraints — those surface inside the dialog.
  const interconnectTrips = useMemo(
    () => deriveCandidatePair(trips, selectedIds),
    [trips, selectedIds],
  )

  // Opens the dialog. If there is a block reason the dialog shows it immediately
  // without calling the API. Otherwise, fetches preview data from the server.
  async function openPreview() {
    if (!interconnectTrips) return
    const { tripA, tripB } = interconnectTrips

    const blockReason = getBlockReason(tripA, tripB)

    if (blockReason) {
      // Show the dialog with the error — no API call needed
      setPreview({ tripA, tripB, interconnectDate: null, routeKm: null, isLoading: false, blockReason })
      return
    }

    setPreview({ tripA, tripB, interconnectDate: null, routeKm: null, isLoading: true, blockReason: null })

    try {
      const res = await authorizedFetch(`${API_URL}/api/trips/interconnect/preview`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ tripAId: tripA.id, tripBId: tripB.id }),
      })
      const data = await res.json() as { interconnectDate: string | null; routeKm: number | null }
      setPreview({ tripA, tripB, interconnectDate: data.interconnectDate, routeKm: data.routeKm, isLoading: false, blockReason: null })
    } catch {
      setPreview(null)
      toast.error("No se pudo cargar el preview de interconexión")
    }
  }

  // Confirms creation — only reachable when blockReason is null.
  async function confirmInterconnect() {
    if (!preview || preview.blockReason) return
    const { tripA, tripB } = preview
    setPreview(prev => prev ? { ...prev, isLoading: true } : null)
    try {
      const res = await authorizedFetch(`${API_URL}/api/trips/interconnect`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ tripAId: tripA.id, tripBId: tripB.id }),
      })
      const body = await res.json() as { id?: string; internalId?: number; error?: string }
      if (!res.ok) throw new Error(body.error ?? "Error al crear el viaje")

      for (const key of options.invalidateKeys) {
        qc.invalidateQueries({ queryKey: key })
      }
      setPreview(null)
      options.onSuccess?.()
      toast.success(
        body.internalId ? `Viaje #${body.internalId} creado` : "Viaje de interconexión creado",
        { description: "Interconexión" },
      )
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al crear el viaje")
      setPreview(prev => prev ? { ...prev, isLoading: false } : null)
    }
  }

  // Closes the dialog. No-op while a creation is in flight.
  function closePreview() {
    if (preview?.isLoading) return
    setPreview(null)
  }

  return {
    // Non-null when exactly 2 trips with the same driver are selected → enables the button.
    interconnectTrips,
    // Non-null while the dialog is open. Check blockReason to know if creation is allowed.
    interconnectPreview: preview,
    // Handlers to wire up to the button and the InterconnectDialog.
    openPreview,
    confirmInterconnect,
    closePreview,
  }
}
